import { gql, useMutation, useQuery } from '@apollo/client';
import IconDocument from '@apollo/icons/default/IconDocument.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import {
  AlertBanner,
  Button,
  ButtonGroup,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
} from '@apollo/orbit';
import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import { HeaderTab } from 'src/components/common/headerTab/HeaderTab';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { useMarkdownTransforms } from 'src/hooks/useMarkdownTransforms';
import {
  StudioEditorContainer,
  useStudioEditor,
} from 'src/hooks/useStudioEditor';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';
import { useInternalGraphLinking } from '../../hooks/useInternalGraphLinking';
import { CollectionEntryFragment } from '../OperationCollectionsSection';
import { ReadmeMarkdown } from '../readmeMarkdown/ReadmeMarkdown';
import { ShortcodesMenu } from '../shortcodes/ShortcodesMenu';

const readmeQuery = gql`
  query ReadmeModalQuery(
    $graphId: ID!
    $graphVariant: String!
    $isProposalRoute: Boolean!
  ) {
    service(id: $graphId) {
      id
      variant(name: $graphVariant) {
        id
        readme @skip(if: $isProposalRoute) {
          id
          content
        }
        proposal @include(if: $isProposalRoute) {
          id
          description
        }
      }
    }
  }
`;

const updateReadmeMutation = gql`
  mutation UpdateVariantReadmeMutation(
    $readmeContent: String!
    $graphVariant: String!
    $graphId: ID!
  ) {
    service(id: $graphId) {
      variant(name: $graphVariant) {
        updateVariantReadme(readme: $readmeContent) {
          id
          readme {
            id
            content
          }
        }
      }
    }
  }
`;

const updateProposalDescriptionMutation = gql`
  mutation UpdateProposalDescriptionMutation(
    $input: UpdateDescriptionInput!
    $graphId: ID!
    $graphVariant: String!
  ) {
    graph(id: $graphId) {
      variant(name: $graphVariant) {
        proposal {
          ... on ProposalMutation {
            updateDescription(input: $input) {
              ... on Proposal {
                id
                description
              }
            }
          }
        }
      }
    }
  }
`;

export const README_MAX_LENGTH = 100_000;
export const EditReadmeModal = ({
  graphRef,
  graphTitle,
  variantUrl,
  onClose,
  isOpen,
}: {
  graphRef: GraphRef;
  graphTitle: string | undefined;
  variantUrl: string | null | undefined;
  onClose: () => void;
  isOpen: boolean;
}) => {
  const { isProposalRoute } = useInternalGraphLinking();
  const { graphId, graphVariant } = graphRef;
  const [tab, setTab] = useState<'markdown' | 'preview'>('markdown');
  const [menuOpen, setMenuOpen] = useState(true);

  const contentContainer = useRef<HTMLDivElement>(null);
  const leftPaneWidth = 300;

  const isProposalRouteAndFlagIsOn = isProposalRoute;

  const { data: readmeContent, loading: loadingReadme } = useQuery<
    GraphQLTypes.ReadmeModalQuery,
    GraphQLTypes.ReadmeModalQueryVariables
  >(readmeQuery, {
    variables: {
      graphId,
      graphVariant,
      isProposalRoute: isProposalRouteAndFlagIsOn,
    },
  });

  const [charCount, setCharCount] = useState(0);
  const markdownEditor = useStudioEditor({
    language: 'markdown',
    defaultValue: '',
    glyphMargin: false,
    onChange: (newValue: string, _) => setCharCount(newValue.length),
  });
  const savedReadMeContent = isProposalRoute
    ? readmeContent?.service?.variant?.proposal?.description
    : readmeContent?.service?.variant?.readme?.content;
  useEffect(() => {
    if (savedReadMeContent) {
      markdownEditor.setValue(savedReadMeContent);
    }
  }, [markdownEditor, savedReadMeContent]);

  useEffect(() => {
    if (contentContainer?.current) {
      const containerWidth = contentContainer?.current?.clientWidth;
      const containerHeight = contentContainer?.current?.clientHeight;
      const newRightPaneWidth = menuOpen
        ? containerWidth - leftPaneWidth
        : containerWidth;

      markdownEditor.layout({
        width: newRightPaneWidth,
        height: containerHeight,
      });
    }
  }, [markdownEditor, menuOpen, tab, leftPaneWidth]);

  const operationCollectionsQuery = useQuery<
    GraphQLTypes.OperationCollectionReadMeQuery,
    GraphQLTypes.OperationCollectionReadMeQueryVariables
  >(
    gql`
      query OperationCollectionReadMeQuery($graphId: ID!, $variant: String!) {
        service(id: $graphId) {
          id
          variant(name: $variant) {
            id
            operationCollections {
              id
              ...CollectionEntryFragment
            }
          }
        }
      }
      ${CollectionEntryFragment}
    `,
    {
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
      variables: {
        graphId,
        variant: graphVariant,
      },
    },
  );

  useMarkdownTransforms({ editor: markdownEditor });

  const [updateReadme, { loading: updating, error }] = useMutation(
    updateReadmeMutation,
    { onCompleted: () => onClose() },
  );

  const [
    updateProposalDescription,
    { loading: updatingProposal, error: updateProposalError },
  ] = useMutation(updateProposalDescriptionMutation, {
    onCompleted: () => onClose(),
  });

  useEffect(() => {
    const exitPromptEventListener = (event: BeforeUnloadEvent) => {
      if (markdownEditor.getValue() !== savedReadMeContent) {
        // WebKit-derived browsers don't follow the spec for the dialog box.
        // https://developer.mozilla.org/en-US/docs/Web/API/BeforeUnloadEvent#examples
        const confirmationMessage = `\o/`;

        // eslint-disable-next-line no-param-reassign
        (event || window.event).returnValue = confirmationMessage;
        return confirmationMessage;
      }
    };

    // Listening for window unload, aka window refreshed or closed
    window.addEventListener('beforeunload', exitPromptEventListener);

    // Removing listener when component unmounts, aka in useEffect callback
    return () => {
      window.removeEventListener('beforeunload', exitPromptEventListener);
    };
  }, [savedReadMeContent, markdownEditor]);

  return (
    <Modal
      autoFocus={false}
      isOpen={isOpen}
      onClose={() => {
        if (markdownEditor.getValue() !== savedReadMeContent) {
          // eslint-disable-next-line no-alert
          window.alert(
            `Are you sure? You have unsaved changes. Click update to save them, or click cancel to exit anyway.`,
          );
        } else {
          onClose();
        }
      }}
      size="6xl"
    >
      <ModalOverlay />
      <ModalContent className="h-[80vh]">
        <ModalHeader
          className="flex-row py-0"
          data-testid="edit-readme-tabs-parent"
        >
          <HeaderTab
            className="text-sm"
            tabID="Markdown"
            isActive={() => tab === 'markdown'}
            onClick={() => {
              setTab('markdown');
              setMenuOpen(true);
            }}
          />
          <HeaderTab
            className="text-sm"
            tabID="Preview"
            isActive={() => tab === 'preview'}
            onClick={() => {
              setTab('preview');
              setMenuOpen(false);
            }}
          />
        </ModalHeader>
        <ModalBody
          className="h-0 flex-1 flex-row gap-0 border-y border-primary p-0"
          ref={contentContainer}
        >
          {updating || loadingReadme || updatingProposal ? (
            <Spinner className="m-auto" size="lg" />
          ) : tab === 'markdown' ? (
            <>
              {menuOpen && (
                <ShortcodesMenu
                  operationCollectionsQuery={operationCollectionsQuery}
                  editor={markdownEditor}
                  onClose={() => setMenuOpen(false)}
                  style={{ width: leftPaneWidth, height: 'auto' }}
                  isProposalRoute={isProposalRoute}
                />
              )}
              <div
                className="relative w-0 flex-1"
                data-testid="edit-readme-editor"
              >
                <StudioEditorContainer
                  editor={markdownEditor}
                  className="h-full"
                />
              </div>
            </>
          ) : (
            <div className="overflow-auto px-6 py-2">
              <ReadmeMarkdown
                content={markdownEditor.getValue()}
                graphTitle={graphTitle}
                graphRef={graphRef}
                variantUrl={variantUrl}
              />
            </div>
          )}
        </ModalBody>
        {error && <ErrorMessage error={error} />}
        {updateProposalError && <ErrorMessage error={updateProposalError} />}
        {charCount > README_MAX_LENGTH - 100 &&
          charCount <= README_MAX_LENGTH && (
            <AlertBanner status="warning">
              Approaching character limit: {charCount} /
              {README_MAX_LENGTH.toLocaleString()}
            </AlertBanner>
          )}
        {charCount > README_MAX_LENGTH && (
          <AlertBanner status="error">
            Cannot update, exceeded character limit: {charCount} /
            {README_MAX_LENGTH.toLocaleString()}
          </AlertBanner>
        )}
        <ModalFooter>
          <Button
            variant="hidden"
            aria-label="graph-shortcode-button"
            className={classnames({
              'pointer-events-none invisible': tab === 'preview',
            })}
            onClick={
              tab === 'markdown' ? () => setMenuOpen(!menuOpen) : () => {}
            }
            isDisabled={tab === 'preview'}
            leftIcon={<IconDocument />}
          >
            Graph shortcodes
          </Button>

          <div className="flex items-center justify-items-center text-sm text-secondary">
            <IconInfo className="mr-1" />
            Changes made to this{' '}
            {isProposalRouteAndFlagIsOn ? 'description' : 'README'} will apply
            only to this {isProposalRoute ? 'proposal' : 'variant'}
          </div>

          <ButtonGroup>
            <Button
              variant="secondary"
              type="submit"
              onClick={onClose}
              isDisabled={updating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              isDisabled={updating || charCount > README_MAX_LENGTH}
              data-testId="graph-readme-submit-update-button"
              type="submit"
              onClick={() => {
                if (!isProposalRoute) {
                  updateReadme({
                    variables: {
                      readmeContent: markdownEditor.getValue(),
                      graphVariant,
                      graphId,
                    },
                  });
                }
                if (isProposalRoute) {
                  updateProposalDescription({
                    variables: {
                      input: {
                        description: markdownEditor.getValue(),
                      },
                      graphId,
                      graphVariant,
                    },
                  });
                }
              }}
            >
              Update
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
